import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Coins, HandCoins, WarningCircle, CheckCircle, Scales, Flag } from "@phosphor-icons/react";
import {
  parseCapitalAsk, assembleCapitalAsk,
  MAX_USE_BULLETS, MAX_MILESTONES,
} from "@/lib/capitalAsk";

/**
 * Structured editor for slide 11 (Capital Structure & The Ask).
 *
 * Iter59 (Feb 2026). Iter72 extends the bullet shape to `{text, pct}`
 * so each use-of-funds line carries a % of the total raise. The form
 * runs a live sanity check that the % values sum to exactly 100 (with
 * a 0.1 tolerance for rounding) and surfaces a persistent banner when
 * they don't.
 */

const C = {
  border: "rgba(255,255,255,0.08)",
  ink: "#F5F7FA",
  muted: "rgba(255,255,255,0.55)",
  bg: "#0F1B22",
  bgSoft: "#132029",
  warn: "#F59E0B",
  good: "#10B981",
};

function parseToLocal(content) {
  const p = parseCapitalAsk(content || "");
  const bullets = (p.bullets || []).map((b) => ({
    text: (b && b.text) || "",
    pct: (b && Number.isFinite(b.pct)) ? b.pct : null,
  }));
  while (bullets.length < MAX_USE_BULLETS) bullets.push({ text: "", pct: null });
  const instrument = p.instrument || { kind: "", note: "" };
  const milestones = [...(p.milestones || [])];
  while (milestones.length < MAX_MILESTONES) milestones.push("");
  return {
    amount: p.amount || "",
    instrument: { kind: instrument.kind || "", note: instrument.note || "" },
    bullets: bullets.slice(0, MAX_USE_BULLETS),
    milestones: milestones.slice(0, MAX_MILESTONES),
  };
}

export default function CapitalAskForm({ content, onChange, primary, currency }) {
  const [amount, setAmount] = useState(() => parseToLocal(content).amount);
  const [instrument, setInstrument] = useState(() => parseToLocal(content).instrument);
  const [bullets, setBullets] = useState(() => parseToLocal(content).bullets);
  const [milestones, setMilestones] = useState(() => parseToLocal(content).milestones);
  const lastEmittedRef = useRef(content || "");

  useEffect(() => {
    if ((content || "") === lastEmittedRef.current) return;
    const next = parseToLocal(content);
    setAmount(next.amount);
    setInstrument(next.instrument);
    setBullets(next.bullets);
    setMilestones(next.milestones);
    lastEmittedRef.current = content || "";
  }, [content]);

  const emit = useCallback((nextAmount, nextInstrument, nextBullets, nextMilestones) => {
    const cleaned = nextBullets.filter((b) => (b.text || "").trim());
    const assembled = assembleCapitalAsk({
      amount: nextAmount,
      instrument: nextInstrument,
      bullets: cleaned,
      milestones: nextMilestones,
    });
    lastEmittedRef.current = assembled;
    onChange(assembled);
  }, [onChange]);

  const updateAmount = (v) => {
    setAmount(v);
    emit(v, instrument, bullets, milestones);
  };
  const updateInstrumentKind = (kind) => {
    // Clear the note if the founder switches away from "both" — it
    // won't apply to a pure equity or pure debt raise.
    const next = { kind, note: kind === "both" ? instrument.note : "" };
    setInstrument(next);
    emit(amount, next, bullets, milestones);
  };
  const updateInstrumentNote = (note) => {
    const next = { ...instrument, note };
    setInstrument(next);
    emit(amount, next, bullets, milestones);
  };
  const updateBulletText = (i, v) => {
    const next = bullets.map((b, idx) => (idx === i ? { ...b, text: v } : b));
    setBullets(next);
    emit(amount, instrument, next, milestones);
  };
  const updateBulletPct = (i, raw) => {
    let pct = null;
    if (raw !== "" && raw != null) {
      const n = parseFloat(raw);
      if (Number.isFinite(n)) pct = Math.max(0, Math.min(100, n));
    }
    const next = bullets.map((b, idx) => (idx === i ? { ...b, pct } : b));
    setBullets(next);
    emit(amount, instrument, next, milestones);
  };
  const updateMilestone = (i, v) => {
    const next = milestones.map((m, idx) => (idx === i ? v : m));
    setMilestones(next);
    emit(amount, instrument, bullets, next);
  };

  // Live-preview trim detection (iter68) — measures against `text` only.
  const [trimmed, setTrimmed] = useState({});
  useLayoutEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    const measure = () => {
      const next = {};
      for (let i = 0; i < bullets.length; i += 1) {
        const src = (bullets[i].text || "").trim();
        if (!src) continue;
        const el = document.querySelector(`[data-testid="capital-use-bullet-preview-${i}"]`);
        if (!el) continue;
        const rendered = (el.textContent || "").trim();
        if (rendered.endsWith("…") && rendered !== src) next[i] = true;
      }
      setTrimmed((prev) => {
        const pk = Object.keys(prev).sort().join(",");
        const nk = Object.keys(next).sort().join(",");
        return pk === nk ? prev : next;
      });
    };
    raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(measure); });
    window.addEventListener("resize", measure);
    let mo = null;
    const previewRoot = document.querySelector('[data-testid="capital-ask-slide"]');
    if (previewRoot && typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(measure);
      mo.observe(previewRoot, { childList: true, subtree: true, characterData: true });
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (mo) mo.disconnect();
    };
  }, [bullets]);

  const displayCurrency = currency || "KES";

  // ── % sanity check ─────────────────────────────────────────────────
  // Sum the pct across bullets that have text (empty rows don't
  // participate) and compare to 100 with a small floating-point
  // tolerance. When any pct is present but the sum ≠ 100, we show a
  // warning banner; when the sum is exactly 100, we surface a subtle
  // "balanced" confirmation so founders know they're done.
  const filledBullets = bullets.filter((b) => (b.text || "").trim());
  const totalPct = filledBullets.reduce((s, b) => s + (Number.isFinite(b.pct) ? b.pct : 0), 0);
  const anyPct = filledBullets.some((b) => Number.isFinite(b.pct) && b.pct > 0);
  const totalIs100 = Math.abs(totalPct - 100) < 0.1;
  const showBalanced = anyPct && totalIs100;
  const showWarn = anyPct && !totalIs100;

  return (
    <div data-testid="capital-ask-form" className="space-y-4"
      style={{ fontFamily: "'Raleway','Manrope',system-ui,sans-serif" }}>
      <div className="p-3 rounded-md text-[13px] leading-relaxed"
        style={{ backgroundColor: `${primary}14`, border: `1px solid ${primary}33`, color: C.ink }}>
        <strong className="font-extrabold">Capital Structure &amp; The Ask</strong> uses a canonical layout — the amount on the left, up to {MAX_USE_BULLETS} use-of-funds bullets on the right. Assign a % of the total raise to each use so funders see the allocation at a glance; the % values must sum to 100.
      </div>

      {/* Capital Ask amount */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <Coins size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              Capital Ask
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              Round size in {displayCurrency} — accepts short-hand like <span className="font-bold">250K</span> or <span className="font-bold">5M</span>.
            </div>
          </div>
        </div>
        {/* Iter97: smart 2-field control — locked currency badge on
            the left (reflecting deck.projections.currency), numeric
            input on the right. Any currency prefix pasted into the
            input is silently stripped so "KES USD 250K" is physically
            impossible to submit. Mirrors the render on the preview slide. */}
        <div
          data-testid="capital-ask-amount-group"
          className="flex items-stretch rounded overflow-hidden focus-within:ring-2 focus-within:ring-offset-0"
          style={{
            border: `1px solid ${C.border}`,
            fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
          }}
        >
          <div
            data-testid="capital-ask-currency-badge"
            className="flex items-center justify-center px-3.5 font-extrabold text-[13px] tracking-wider select-none shrink-0"
            style={{
              backgroundColor: primary,
              color: "#FFFFFF",
              minWidth: 56,
              letterSpacing: "0.05em",
            }}
            title={`Set at the deck level (${displayCurrency}). Change in Financial Projections.`}
          >
            {displayCurrency}
          </div>
          <input
            type="text"
            inputMode="decimal"
            data-testid="capital-ask-amount"
            value={amount}
            onChange={(e) => {
              // Iter97: strip any leading currency code/symbol the founder
              // might paste, keeping only the numeric-ish part. Same
              // regex used on the preview + PPTX render side.
              const cleaned = e.target.value.replace(
                /^\s*(?:USD|EUR|GBP|KES|NGN|ZAR|GHS|UGX|TZS|RWF|XOF|XAF|INR|CAD|AUD|JPY|CNY|CHF|SEK|NOK|DKK|BRL|MXN|AED|SAR|EGP|MAD|ETB)\s+|^\s*[$€£₦₹₵¥]\s*/i,
                "",
              );
              updateAmount(cleaned);
            }}
            placeholder="e.g. 250K"
            className="flex-1 px-3 py-2.5 text-[14px] focus:outline-none"
            style={{
              backgroundColor: C.bg,
              border: "none",
              color: C.ink,
              fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
            }}
          />
        </div>
      </div>

      {/* Instrument — iter73: how the raise is structured (equity /
          debt / both). Segmented control keeps this to one glance; a
          contextual freeform note appears only when the founder picks
          "Both" so they can specify the split without bloating the
          two simple cases. */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <Scales size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              Instrument
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              How is this capital being raised?
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="capital-ask-instrument">
          {[
            { key: "equity", label: "Equity", hint: "SAFE, priced round, common/preferred" },
            { key: "debt",   label: "Debt",   hint: "Convertible note, venture debt, loan" },
            { key: "both",   label: "Both",   hint: "Mix of equity + debt" },
          ].map((opt) => {
            const active = instrument.kind === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                data-testid={`capital-ask-instrument-${opt.key}`}
                onClick={() => updateInstrumentKind(active ? "" : opt.key)}
                className="px-3 py-2.5 rounded-md text-[13px] font-bold text-left transition-colors focus:outline-none"
                style={{
                  backgroundColor: active ? primary : C.bg,
                  border: `1px solid ${active ? primary : C.border}`,
                  color: active ? "#fff" : C.ink,
                }}
                title={opt.hint}
              >
                <div className="font-extrabold">{opt.label}</div>
                <div className="text-[10px] mt-0.5 font-normal"
                  style={{ color: active ? "rgba(255,255,255,0.75)" : C.muted }}>
                  {opt.hint}
                </div>
              </button>
            );
          })}
        </div>
        {instrument.kind === "both" && (
          <div className="mt-3">
            <label className="text-[10.5px] uppercase tracking-widest font-bold" style={{ color: C.muted }}>
              Split (optional)
            </label>
            <input
              type="text"
              data-testid="capital-ask-instrument-note"
              value={instrument.note || ""}
              onChange={(e) => updateInstrumentNote(e.target.value)}
              placeholder="e.g. 60% equity, 40% venture debt"
              className="mt-1 w-full px-3 py-2 rounded text-[13px] focus:outline-none"
              style={{
                backgroundColor: C.bg,
                border: `1px solid ${C.border}`,
                color: C.ink,
                fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
              }}
            />
          </div>
        )}
      </div>

      {/* Use of Funds — bullets + % */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <HandCoins size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              Use of Funds
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              {MAX_USE_BULLETS} bullets on how the cash will be spent, each with a % allocation. The preview trims long bullet text and we&apos;ll flag it when it does.
            </div>
          </div>
          {/* Live % total pill */}
          <div
            data-testid="capital-use-pct-total"
            className="shrink-0 rounded-full px-3 py-1.5 text-[11px] font-extrabold tabular-nums font-mono-num"
            style={{
              backgroundColor: showBalanced
                ? "rgba(16,185,129,0.12)"
                : showWarn ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.06)",
              color: showBalanced ? C.good : showWarn ? C.warn : C.muted,
              border: `1px solid ${showBalanced ? C.good : showWarn ? C.warn : C.border}`,
            }}
            title="Total across all bullets — must sum to 100%"
          >
            {totalPct.toFixed(totalPct % 1 === 0 ? 0 : 1)}% / 100%
          </div>
        </div>

        {/* Sanity banner */}
        {showWarn && (
          <div
            data-testid="capital-use-pct-warn"
            className="mb-3 rounded-md p-2.5 flex items-center gap-2 text-[12px]"
            style={{ backgroundColor: "rgba(245,158,11,0.10)", border: `1px solid ${C.warn}44`, color: C.warn }}
          >
            <WarningCircle size={14} weight="fill" />
            <span>
              Use-of-funds allocation is <span className="font-extrabold">{totalPct.toFixed(1)}%</span> — needs to add up to <span className="font-extrabold">100%</span>. Adjust the % on one or more bullets.
            </span>
          </div>
        )}
        {showBalanced && (
          <div
            data-testid="capital-use-pct-ok"
            className="mb-3 rounded-md p-2.5 flex items-center gap-2 text-[12px]"
            style={{ backgroundColor: "rgba(16,185,129,0.08)", border: `1px solid ${C.good}44`, color: C.good }}
          >
            <CheckCircle size={14} weight="fill" />
            <span>Allocations balance to 100%. Nicely done.</span>
          </div>
        )}

        <div className="space-y-2">
          {bullets.map((b, i) => {
            const wasTrimmed = !!trimmed[i];
            return (
              <div key={i} className="flex items-start gap-2">
                <span className="mt-3 shrink-0 rounded-full"
                  style={{ width: 6, height: 6, backgroundColor: primary }} />
                <div className="flex-1 min-w-0 flex gap-2">
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      data-testid={`capital-use-bullet-${i}`}
                      value={b.text}
                      onChange={(e) => updateBulletText(i, e.target.value)}
                      placeholder={i === 0
                        ? "e.g. Product & engineering hires (2 senior full-stack, 1 designer)"
                        : "Another use of the raise…"}
                      className="w-full px-3 py-2 rounded text-[14px] focus:outline-none"
                      style={{
                        backgroundColor: C.bg,
                        border: `1px solid ${wasTrimmed ? C.warn : C.border}`,
                        color: C.ink,
                        fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
                      }}
                    />
                    {wasTrimmed && (
                      <div
                        data-testid={`capital-use-bullet-${i}-trim-warn`}
                        className="mt-1 flex items-center gap-1 text-[10.5px]"
                        style={{ color: C.warn }}
                      >
                        <WarningCircle size={12} weight="fill" />
                        The preview is trimming this bullet — shorten it to keep the tail visible.
                      </div>
                    )}
                  </div>
                  {/* % input — width fixed so the row stays visually balanced */}
                  <div className="shrink-0" style={{ width: 96 }}>
                    <div className="relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        data-testid={`capital-use-bullet-${i}-pct`}
                        value={b.pct == null ? "" : b.pct}
                        onChange={(e) => updateBulletPct(i, e.target.value)}
                        placeholder="0"
                        disabled={!(b.text || "").trim()}
                        className="w-full pl-3 pr-7 py-2 rounded text-[14px] text-right focus:outline-none font-mono-num tabular-nums disabled:opacity-40"
                        style={{
                          backgroundColor: C.bg,
                          border: `1px solid ${C.border}`,
                          color: C.ink,
                        }}
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-[13px]"
                        style={{ color: C.muted }}>
                        %
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Milestones — what this raise unlocks */}
      <div className="p-4 rounded-lg" style={{ backgroundColor: C.bgSoft, border: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2.5 mb-3">
          <span className="inline-flex items-center justify-center rounded-full shrink-0"
            style={{ width: 32, height: 32, backgroundColor: primary, color: "#fff" }}>
            <Flag size={16} weight="fill" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-extrabold uppercase tracking-[0.20em]" style={{ color: primary }}>
              Milestones
            </div>
            <div className="text-[12px]" style={{ color: C.muted }}>
              Up to {MAX_MILESTONES} concrete outcomes this raise unlocks — funders want to see what their money buys, e.g. <span className="font-bold">Reach 10K paying customers — Q2 2027</span>.
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {milestones.map((m, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="shrink-0 inline-flex items-center justify-center rounded-full font-extrabold text-[11px]"
                style={{ width: 22, height: 22, backgroundColor: "#ECC764", color: "#004B62" }}>
                {i + 1}
              </span>
              <input
                type="text"
                data-testid={`capital-milestone-${i}`}
                value={m}
                onChange={(e) => updateMilestone(i, e.target.value)}
                placeholder={i === 0 ? "e.g. Launch in 2 new counties — Q1 2027" : "Another milestone…"}
                className="flex-1 px-3 py-2 rounded text-[14px] focus:outline-none"
                style={{
                  backgroundColor: C.bg,
                  border: `1px solid ${C.border}`,
                  color: C.ink,
                  fontFamily: "'Raleway','Manrope',system-ui,sans-serif",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
